/**
 * Tiny i18n runtime.
 *
 * Usage:
 *   import { t, setLang, getLang, LANGS } from '../i18n';
 *   t('hud_tap_to_breach') => 'tap any node to breach' (etc per locale)
 *
 * Persistence: chosen language saved in localStorage under 'nodefall.lang'.
 * Missing keys fall back to English. Adding a language: drop a file under
 * src/i18n/, register it in `BUNDLES` below.
 */

import { en, type LangStrings, type StringKey } from './en';
import { es } from './es';
import { fr } from './fr';
import { de } from './de';
import { pt } from './pt';
import { it } from './it';
import { ja } from './ja';
import { ko } from './ko';
import { zh } from './zh';
import { ru } from './ru';

export type LangCode = 'en' | 'es' | 'fr' | 'de' | 'pt' | 'it' | 'ja' | 'ko' | 'zh' | 'ru';

export interface LangMeta {
  code: LangCode;
  nativeLabel: string;
  asciiLabel: string;
}

export const LANGS: LangMeta[] = [
  { code: 'en', nativeLabel: 'EN',     asciiLabel: 'ENGLISH' },
  { code: 'es', nativeLabel: 'ES',     asciiLabel: 'ESPAÑOL' },
  { code: 'fr', nativeLabel: 'FR',     asciiLabel: 'FRANÇAIS' },
  { code: 'de', nativeLabel: 'DE',     asciiLabel: 'DEUTSCH' },
  { code: 'pt', nativeLabel: 'PT',     asciiLabel: 'PORTUGUÊS' },
  { code: 'it', nativeLabel: 'IT',     asciiLabel: 'ITALIANO' },
  { code: 'ja', nativeLabel: '日本語', asciiLabel: 'JAPANESE' },
  { code: 'ko', nativeLabel: '한국어', asciiLabel: 'KOREAN' },
  { code: 'zh', nativeLabel: '中文',   asciiLabel: 'CHINESE' },
  { code: 'ru', nativeLabel: 'RU',     asciiLabel: 'РУССКИЙ' },
];

const BUNDLES: Record<LangCode, LangStrings> = {
  en, es, fr, de, pt, it, ja, ko, zh, ru,
};

const LANG_KEY = 'nodefall.lang';

let currentLang: LangCode = loadLang();

function loadLang(): LangCode {
  try {
    const v = localStorage.getItem(LANG_KEY);
    if (v && (v in BUNDLES)) return v as LangCode;
    // Auto-detect from browser
    const sys = navigator.language?.toLowerCase() ?? 'en';
    if (sys.startsWith('es')) return 'es';
    if (sys.startsWith('fr')) return 'fr';
    if (sys.startsWith('de')) return 'de';
    if (sys.startsWith('pt')) return 'pt';
    if (sys.startsWith('it')) return 'it';
    if (sys.startsWith('ja')) return 'ja';
    if (sys.startsWith('ko')) return 'ko';
    if (sys.startsWith('zh')) return 'zh';
    if (sys.startsWith('ru')) return 'ru';
  } catch { /* ignore */ }
  return 'en';
}

export function setLang(code: LangCode) {
  currentLang = code;
  try { localStorage.setItem(LANG_KEY, code); } catch { /* ignore */ }
}

export function getLang(): LangCode {
  return currentLang;
}

/** Lookup a string by key. Falls back to English if missing in current lang. */
export function t(key: StringKey): string {
  const bundle = BUNDLES[currentLang] ?? en;
  return bundle[key] ?? en[key] ?? String(key);
}

/** Same as t() but with {placeholder} substitution. */
export function tf(key: StringKey, vars: Record<string, string | number>): string {
  let s = t(key);
  for (const [k, v] of Object.entries(vars)) {
    s = s.replace(new RegExp('\\{' + k + '\\}', 'g'), String(v));
  }
  return s;
}

/**
 * Font stack that includes fallbacks for CJK + Cyrillic. Use this everywhere
 * instead of the bare 'ui-monospace, monospace' so non-Latin glyphs render
 * correctly on Windows + macOS browsers.
 */
export const FONT_STACK =
  'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", ' +
  '"Yu Gothic", "Microsoft YaHei", "Malgun Gothic", "Hiragino Sans", monospace';
